import _ from '@sparta.global.vendors/lodash';
import spartaMessageUtility from '@sparta.global.utilities/sparta-messaging-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';

/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["content"] }] */

/**
 * @name        Ah Commons Utility
 * @file        ah-common-utility.js
 * @description common methods for CAW
 * @classdesc   common methods for CAW
 * @global      Creates a global AhCommonUtility object
 * @author      zkos9ab
 */

const MESSAGE_TYPE = {
  SUCCESS: 'Success'
};
class AhCommonUtility {
  constructor(options) {
    this.options = options || {};
    this.displayMessageUtilList = {};
  }

  /**
    * @function getBasePath
    * @description  Hide the page level message which displayed based on the element.
    * @param {Object} spaParams - Widget/Site level this.spaParams.
    * @returns {String} - It will return the basedpath for API calls which took from widget/site config
  */
  getBasePath(spaParams) {
    return _.get(spaParams, 'siteParams.ahServiceEndpoint') || _.get(spaParams, 'siteParams.serviceEndpoint') || _.get(spaParams, 'siteParams.domain.hostname');
  }

  /**
    * @function getEndPoints
    * @description  It combine the basepath/ fallback(if basepath not avaialble host origin consider as fallback) and the API urls list based on params.
    * @param {Object} endPoints - List of API urls relative paths.
    * @param {String} _basePath - Basepath to append in the API urls.
    * @returns {Object} - It will API urls with base path details appened in each API url
  */
  getEndPoints(endPoints, _basePath) {
    const fallbackPath = window.location.origin;
    const basePath = _basePath || fallbackPath;
    const apiEndPoints = {};
    Object.keys(endPoints).forEach(key => {
      apiEndPoints[key] = basePath + endPoints[key];
    });
    return apiEndPoints;
  }

  /**
    * @function includeExternalResource
    * @description  It helps to include
    * @param {Object} externalResourceObject - external resource object param.
    * @param {String} type - which resouece injection (style/script).
    * @param {String} path - External Resource path.
    * @param {Function} callback - Function to trigger when the external resource is loaded/failed.
    * @param {Boolean} triggerCallbackOnFailure - Boolean to notify if resource load is failed whether callback need to trigger or not.
    * @returns {undefined}
  */
  includeExternalResource({
    type,
    path,
    callback,
    triggerCallbackOnFailure = false
  }) {
    console.log(`Loading :  ${path}`);
    let element;
    if (type === 'script') {
      element = document.createElement('script');
      element.setAttribute('src', path);
      element.setAttribute('async', 'true');
    } else if (type === 'style') {
      element = document.createElement('link');
      element.setAttribute('rel', 'stylesheet');
      element.setAttribute('type', 'text/css');
      element.setAttribute('href', path);
      element.setAttribute('async', 'true');
    } else {
      console.log(`Not loading. Unknown TYPE: ${type}`);
      return;
    }
    element.onreadystatechange = element.onload = function () {
      if (callback && !callback.done && (!element.readyState || /loaded|complete/.test(element.readyState))) {
        callback.done = true;
        callback();
      }
    };
    element.onerror = function () {
      if (triggerCallbackOnFailure === true && callback && !callback.done) {
        console.log(`Unable to load: ${path}. Still triggering callback.`);
        callback.done = true;
        callback();
      } else {
        console.log(`Unable to load: ${path}. Callback not triggered.`);
      }
    };
    const head = document.getElementsByTagName('head')[0] || document.body;
    head.appendChild(element);
  }

  /**
    * @function trackCoreMetricesError
    * @description  Core Metrics capture for error scenarios.
    * @param {Object} errorInfo - Error details Info.
    * @param {Object} errorInfo.errorCode - ErrorCode to record the metrics.
    * @param {Object} errorInfo.errorMessage - Error message corresponding to errorcode to record the metrics.
    * @returns {undefined}
  */
  trackCoreMetricesError({
    errorCode,
    errorMessage
  }) {
    typeof bactm_captureCustomError === 'function' && bactm_captureCustomError(errorCode, errorMessage);
  }

  /**
    * @function toggleEnableDisableFormFields
    * @description  It will disable/enable the input and select fields and button from the given parent formelemnt.
    * @param {Object} formDetails - Object data to disable/enable the form.
    * @param {Object} formDetails.formElement - The name of the custom event.
    * @param {Boolean} formDetails.isDisabled - To update whether field need to disable or enable, on default its false.
    * @returns {undefined}
  */
  toggleEnableDisableFormFields({
    formElement,
    isDisabled = false
  }) {
    const inputElementList = formElement.querySelectorAll('input');
    const selectElementList = formElement.querySelectorAll('select');
    const buttonElementList = formElement.querySelectorAll('button');
    const formInputFields = [...inputElementList, ...selectElementList, ...buttonElementList];
    formInputFields.forEach(fieldElement => {
      fieldElement.disabled = isDisabled;
    });
  }

  /**
    * @function displayPageLevelMessaging
    * @description  It will display the messaging in module level based on the element added.
    * @param {Object} messagingDetails - Object data to display the messaging.
    * @param {String} messagingDetails.elementId - Element ID/Class where the displaying message in target element.
    * @param {String} messagingDetails.title - Title/Header of the message if anything needed to display.
    * @param {String | Array} messagingDetails.message - Respective message to display, For list of messages use Array of Strings.
    * @param {String} messagingDetails.type - Type of the message (Success/Error/Info/Help/Warning).
    * @param {Object} messagingDetails.option - To update whether field need to disable or enable, on default its false.
    * @param {String} messagingDetails.option.size - size of the message to display ('large', 'medium', 'small', or 'xsmall').
    * @param {String} messagingDetails.option.listType - 'ordered', 'unordered', or 'paragraph'. Applicable if message contains an array of messages.
    * @param {String} messagingDetails.option.insert - 'before', 'after', or 'replace'. Controls positioning of message with respect to target element..
    * @param {Boolean} messagingDetails.option.dismiss - Adds an "X" button to message allowing for it to be closed.
    * 
    * @returns {undefined}
  */
  displayPageLevelMessaging({
    elementId,
    title = '',
    message = ' ',
    type = MESSAGE_TYPE.SUCCESS,
    option = {}
  }) {
    this.hidePageLevelMessaging();
    if (!this.displayMessageUtilList[elementId]) {
      this.displayMessageUtilList[elementId] = new spartaMessageUtility();
    }
    if (elementId) {
      const messagingUtilInstance = this.displayMessageUtilList[elementId];
      messagingUtilInstance[`show${type}MessageInsideFirst`](elementId, title, message, {
        ...option
      });
      if (this._isElementAvailable(elementId)) {
        document.querySelector(elementId).focus();
      } else {
        console.error('Provided Element ID/Class is not available in the DOM. Kindly validate it');
      }
    } else {
      console.error('ElementID is mandatory to display the message content');
    }
  }

  /**
    * @function hidePageLevelMessaging
    * @description  Hide the page level message which displayed based on the element.
    * @param {String} elementId - Messaging element id/class to remove the messaging.
    * @returns {undefined}
  */
  hidePageLevelMessaging(elementId) {
    if (this.displayMessageUtilList[elementId]) {
      this.displayMessageUtilList[elementId].destroy();
    }
  }

  /**
    * @function _isElementAvailable
    * @description  check whether the element is available in DOM or not.
    * @param {String} selectorId - Messaging element id/class to remove the messaging.
    * @returns {undefined}
  */
  _isElementAvailable(selectorId) {
    return !!document.querySelector(selectorId);
  }

  /**
    * @function updateDomain
    * @description  It helps to update the domain basepath and apiEndpoints based on client payload.
    * @param {String} STORE_KEY - Store key to get the sparta store data.
    * @returns {undefined}
  */
  updateDomain(STORE_KEY) {
    const {
      domain
    } = spartaStoreUtility.getData(STORE_KEY);
    this.basePath = domain || this.basePath;
    this.apiEndPoints = ahCommonUtility.getEndPoints(this.endpoints, this.basePath);
  }
  hbsJCRCustomUpdate(modifyContent) {
    if (modifyContent) {
      Object.keys(modifyContent).forEach(elementId => {
        const queryId = `#${elementId}`;
        if (this._isElementAvailable(queryId)) {
          document.querySelector(queryId).innerHTML = modifyContent[elementId];
        }
      });
    }
  }

  /**
    * @function behBioChangeContext
    * @description  Update the context name if BioCatch SDK is already integrated in page level.
    * @param {String} contextName - Name of the context to be updated in wup calls payload.
    * @returns {undefined}
  */
  behBioChangeContext(contextName) {
    if (typeof cdApi === 'object') {
      cdApi.changeContext(contextName);
    }
  }
}

/**
 * @const AhCommonUtility
 * @description single instance of AhCommonUtility used to expose this utility
 */
const ahCommonUtility = new AhCommonUtility();

export { ahCommonUtility as default };
